import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, generatePath } from 'react-router-dom'
import { useIntl } from 'react-intl'

import ROUTES from 'app/routes'
import { commonMessages } from 'app/i18n/messages'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useCurrentHomeSiteId,
  useNetworkHomeJobsRouteParams,
} from 'features/networkAi/hooks'
import {
  claimDevicesFetchDataSelector,
  siteWlansFetchDataSelector,
  subsribedAccessPointsCountSelector,
} from 'features/networkAi/selectors'
import { fetchSiteWlansThunk } from 'features/networkAi/slice'
import { SpinnerCentered } from 'components/Loader'

import NetworkWizardLayout from '../../NetworkWizardLayout'
import {
  NetworkWizardCancelButton,
  NetworkWizardSubmitButton,
  NetworkWizardTitle,
} from '../../styles'
import {
  CreateWLANsButton,
  CreateWLANsContainer,
  CreateWLANsHeaderWrapper,
  StyledCheckbox,
  StyledNetworkWizardBottomPanel,
} from '../styles'
import CreateWlanCollapse from '../CreateWLANCollapse'
import CreateWLANForm from '../CreateWLANForm'
import { SiteWlan } from 'features/networkAi/types'
import { NEW_WLAN_NAME } from '../CreateWLANForm/consts'

function CreateWLANsSetup() {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const { rootDomainId, jobId, homeId } = useNetworkHomeJobsRouteParams()
  const siteWlans = useAppSelector(siteWlansFetchDataSelector.data)
  const isLoading = useAppSelector(siteWlansFetchDataSelector.isFetching)
  const claimDevices = useAppSelector(claimDevicesFetchDataSelector.data)
  const subscribedCount = useAppSelector(subsribedAccessPointsCountSelector)
  const siteId = useCurrentHomeSiteId({ homeId })
  const [saveAsTemplate, setSaveAsTemplate] = useState(false)

  const siteWlansWithNew: SiteWlan[] = Array.isArray(siteWlans)
    ? siteWlans.concat({} as SiteWlan)
    : [{} as SiteWlan]

  useEffect(() => {
    siteId && dispatch(fetchSiteWlansThunk(siteId))
  }, [dispatch, siteId])

  const handleChangeCheckbox = useCallback((event) => {
    setSaveAsTemplate(event.target.checked)
  }, [])

  const handleBackButtonClick = useCallback(() => {
    const path = generatePath(ROUTES.networkAi.wizard.devicesList, {
      rootDomainId,
      jobId,
      homeId,
    })

    return history.push(path)
  }, [history, rootDomainId, jobId, homeId])

  const handleNextDoneClick = useCallback(() => {
    if (!saveAsTemplate) {
      const path = generatePath(ROUTES.networkAi.dashboard.root, {
        rootDomainId,
        jobId,
        homeId,
      })

      return history.push(path)
    } else {
      const path = generatePath(ROUTES.networkAi.wizard.saveTemplate, {
        rootDomainId,
        jobId,
        homeId,
      })

      return history.push(path)
    }
  }, [history, rootDomainId, jobId, homeId, saveAsTemplate])

  const bottomBlock = useMemo(() => {
    return (
      <StyledNetworkWizardBottomPanel>
        <StyledCheckbox
          labelText="Save all as Template"
          checked={saveAsTemplate}
          onChange={handleChangeCheckbox}
        />

        <NetworkWizardCancelButton
          variant="secondary"
          type="button"
          onClick={handleBackButtonClick}
        >
          {formatMessage(commonMessages.back)}
        </NetworkWizardCancelButton>
        <NetworkWizardSubmitButton
          variant="primary"
          type="submit"
          disabled={false}
          onClick={handleNextDoneClick}
        >
          {!saveAsTemplate ? 'Done' : formatMessage(commonMessages.next)}
        </NetworkWizardSubmitButton>
      </StyledNetworkWizardBottomPanel>
    )
  }, [
    saveAsTemplate,
    handleChangeCheckbox,
    handleBackButtonClick,
    handleNextDoneClick,
    formatMessage,
  ])

  return (
    <NetworkWizardLayout
      bottomBlock={bottomBlock}
      currentWizardStepId={4}
      hasError={subscribedCount !== claimDevices?.length}
    >
      <CreateWLANsContainer>
        <CreateWLANsHeaderWrapper>
          <NetworkWizardTitle>WLANs</NetworkWizardTitle>
          <CreateWLANsButton variant="primary" disabled>
            Add New WLAN
          </CreateWLANsButton>
        </CreateWLANsHeaderWrapper>

        {isLoading ? (
          <SpinnerCentered mt="200px" />
        ) : (
          siteWlans &&
          siteWlansWithNew.map((wlan, i) => {
            return (
              <CreateWlanCollapse
                key={wlan.id + i}
                label={wlan.ssid || NEW_WLAN_NAME}
                wlan={wlan}
                siteId={siteId}
                defaultExpanded={i === siteWlansWithNew.length - 1}
              >
                <CreateWLANForm
                  key={wlan.id + i}
                  wlan={wlan}
                  siteId={siteId}
                  rootDomainId={rootDomainId}
                  jobId={jobId}
                  homeId={homeId}
                  wlanName={wlan.ssid || NEW_WLAN_NAME}
                  defaultExpanded={i === siteWlansWithNew.length - 1}
                  wizardMode={true}
                  onSubmit={() => siteWlans.push({} as SiteWlan)}
                />
              </CreateWlanCollapse>
            )
          })
        )}
      </CreateWLANsContainer>
    </NetworkWizardLayout>
  )
}

export default CreateWLANsSetup
